import { C1AutocompleteMultipleAsyncHook, renderTagsWithLocked } from 'components/core/autocomplete'
import { fieldLabelString } from 'components/core/dynamic-form/fields/FieldLabel'
import { UnknownField } from 'components/core/dynamic-form/fields/RenderField'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource/ResourceDisplayName'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppResourceValue } from 'lib/actions/apps/app-resources'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user'
import { AppResourceRef, SearchAppResourcesRequest } from 'pbts/c1/api/app/v1/app_resource'
import { AppUserRef, AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { AppResourceFilter, AppUserFilter, C1UserFilter, Field, PickerField } from 'pbts/c1/api/form/v1/form'
import { SearchUsersRequest, UserRef } from 'pbts/c1/api/user/v1/user'
import { FC, ReactNode, useMemo } from 'react'
import { ControllerRenderProps, FieldErrors } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { appResourceSearchApi } from 'redux_toolkit/app-resources'
import { searchAppUsersApi } from 'redux_toolkit/app-users'
import { userSearchApi } from 'redux_toolkit/users'

type RenderMultiPickerFieldProps = {
    field: Field
    cField: ControllerRenderProps
    errors: FieldErrors
    disabled: boolean
    lockedValues?: string[]
    pickerField: PickerField
}

export const RenderMultiPickerField: FC<RenderMultiPickerFieldProps> = ({
    field,
    cField,
    errors,
    disabled,
    lockedValues,
    pickerField,
}) => {
    switch (pickerField.type.oneofKind) {
        case 'appUserPicker':
            return <RenderAppUserPicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                lockedValues={lockedValues}
                appUserPicker={pickerField.type.appUserPicker}
            />
        case 'resourcePicker':
            return <RenderResourcePicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                lockedValues={lockedValues}
                resourcePicker={pickerField.type.resourcePicker}
            />
        case 'c1UserPicker':
            return <RenderC1UserPicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                lockedValues={lockedValues}
                c1UserPicker={pickerField.type.c1UserPicker}
            />
        default:
            return <UnknownField field={field} />
    }
}

type RenderAppUserPickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: FieldErrors
    disabled: boolean
    lockedValues?: string[]
    appUserPicker: AppUserFilter
}

const RenderAppUserPicker: FC<RenderAppUserPickerProps> = ({ field, cField, errors, disabled, lockedValues, appUserPicker }) => {
    const intl = useIntl()
    const { appId } = appUserPicker
    const initialIds = cField.value as string[] | undefined

    const initialRefs = useMemo(() => {
        if (!initialIds?.length) return undefined
        return initialIds.map((id) => AppUserRef.create({ id, appId }))
    }, [initialIds, appId])

    const appUserReq = useMemo(() => ({
        create: (q: Partial<AppUserServiceSearchRequest>): AppUserServiceSearchRequest =>
            AppUserServiceSearchRequest.create({
                ...q,
                appId: appId || undefined,
            }),
    }), [appId])

    const renderTags = useMemo(() => {
        if (!lockedValues?.length) return undefined
        return renderTagsWithLocked<AppUserValue>(lockedValues)
    }, [lockedValues])

    const renderAppUserOption = (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: AppUserValue,
    ): ReactNode => (
        <li {...props} key={option.id}>
            <AppUserDisplayName
                appUser={option}
                app={option.app}
                readOnly
            />
        </li>
    )

    return <C1AutocompleteMultipleAsyncHook<AppUserServiceSearchRequest, AppUserValue>
        hook={searchAppUsersApi.useLazySearch}
        Req={appUserReq}
        initialRefs={initialRefs}
        onSelected={(appUsers): void => {
            const selectedIds = appUsers.map((u) => u.id)
            // Ensure locked values are always included
            lockedValues?.forEach((locked) => {
                if (!selectedIds.includes(locked)) {
                    selectedIds.unshift(locked)
                }
            })
            cField.onChange(selectedIds)
        }}
        error={!!errors[field.name]}
        disabled={disabled}
        onBlur={cField.onBlur}
        placeholder={intl.formatMessage({
            id: 'multi_picker_app_user_placeholder',
            defaultMessage: 'Select accounts',
        })}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
        renderTags={renderTags}
        renderOption={renderAppUserOption}
    />
}

type RenderResourcePickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: FieldErrors
    disabled: boolean
    lockedValues?: string[]
    resourcePicker: AppResourceFilter
}

const renderResourceOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppResourceValue,
): ReactNode => (
    <li {...props} key={`${option.appId}-${option.id}`}>
        <ResourceDisplayName
            resource={option}
            resourceType={option.appResourceType}
            appName={option.app?.displayName}
            includeDetails
            includeType
            readOnly
        />
    </li>
)

const RenderResourcePicker: FC<RenderResourcePickerProps> = ({ field, cField, errors, disabled, lockedValues, resourcePicker }) => {
    const intl = useIntl()
    const { appId, resourceTypeId } = resourcePicker
    const initialIds = cField.value as string[] | undefined

    const initialRefs = useMemo(() => {
        if (!initialIds?.length) return undefined
        return initialIds.map((id) => AppResourceRef.create({ id, appId, appResourceTypeId: resourceTypeId }))
    }, [initialIds, appId, resourceTypeId])

    const resourceReq = useMemo(() => ({
        create: (q: Partial<SearchAppResourcesRequest>): SearchAppResourcesRequest =>
            SearchAppResourcesRequest.create({
                ...q,
                appId: appId || undefined,
                resourceTypeIds: resourceTypeId ? [resourceTypeId] : undefined,
            }),
    }), [appId, resourceTypeId])

    const renderTags = useMemo(() => {
        if (!lockedValues?.length) return undefined
        return renderTagsWithLocked<AppResourceValue>(lockedValues)
    }, [lockedValues])

    return <C1AutocompleteMultipleAsyncHook<SearchAppResourcesRequest, AppResourceValue>
        hook={appResourceSearchApi.useLazySearchAppResources}
        Req={resourceReq}
        initialRefs={initialRefs}
        onSelected={(resources): void => {
            const selectedIds = resources.map((r) => r.id)
            // Ensure locked values are always included
            lockedValues?.forEach((locked) => {
                if (!selectedIds.includes(locked)) {
                    selectedIds.unshift(locked)
                }
            })
            cField.onChange(selectedIds)
        }}
        error={!!errors[field.name]}
        disabled={disabled}
        onBlur={cField.onBlur}
        placeholder={intl.formatMessage({
            id: 'multi_picker_resource_placeholder',
            defaultMessage: 'Select resources',
        })}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
        renderTags={renderTags}
        renderOption={renderResourceOption}
    />
}

type RenderC1UserPickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: FieldErrors
    disabled: boolean
    lockedValues?: string[]
    c1UserPicker: C1UserFilter
}

const renderC1UserOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: UserValue,
): ReactNode => (
    <li {...props} key={option.id}>
        <UserDisplayName user={option} readOnly />
    </li>
)

const RenderC1UserPicker: FC<RenderC1UserPickerProps> = ({ field, cField, errors, disabled, lockedValues }) => {
    const intl = useIntl()
    const initialIds = cField.value as string[] | undefined

    const initialRefs = useMemo(() => {
        if (!initialIds?.length) return undefined
        return initialIds.map((id) => UserRef.create({ id }))
    }, [initialIds])

    const userReq = useMemo(() => ({
        create: (q: Partial<SearchUsersRequest>): SearchUsersRequest =>
            SearchUsersRequest.create({
                ...q,
            }),
    }), [])

    const renderTags = useMemo(() => {
        if (!lockedValues?.length) return undefined
        return renderTagsWithLocked<UserValue>(lockedValues)
    }, [lockedValues])

    return <C1AutocompleteMultipleAsyncHook<SearchUsersRequest, UserValue>
        hook={userSearchApi.useLazySearch}
        Req={userReq}
        initialRefs={initialRefs}
        onSelected={(users): void => {
            const selectedIds = users.map((u) => u.id)
            // Ensure locked values are always included
            lockedValues?.forEach((locked) => {
                if (!selectedIds.includes(locked)) {
                    selectedIds.unshift(locked)
                }
            })
            cField.onChange(selectedIds)
        }}
        error={!!errors[field.name]}
        disabled={disabled}
        onBlur={cField.onBlur}
        placeholder={intl.formatMessage({
            id: 'multi_picker_c1_user_placeholder',
            defaultMessage: 'Select users',
        })}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
        renderTags={renderTags}
        renderOption={renderC1UserOption}
    />
}
